import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, TextField, Chip } from "@material-ui/core";
import { useGlAllowed } from "../gl-shared/gl-status";
import * as glApiDefault from "../gl-shared/gl-api";
import "./gl-card-review-drawer.scss";
import { REPLAY_MASK } from "../../utils/replay-mask";

// The app theme's contained-primary (bg + contrastText) measures < 4.5:1; inline
// styles win the cascade unconditionally, so GL primary actions are AA-safe
// (#0d47a1 on #fff ~ 8.6:1) regardless of theme overrides.
const AA_PRIMARY = { backgroundColor: "#0d47a1", color: "#fff" };

const TERMINAL_COPY = {
  unavailable: "Couldn't load your review cards. Try again.",
  disabled: "Review cards aren't available right now.",
  cap: "You've reached today's limit.",
  conflict: "This card changed since you opened it."
};

/**
 * GL-4b card review drawer sheet (GL-1 §2). Post-grade, additive + DARK: renders
 * null unless the GL flag+audience are on AND the question has been graded. Lists
 * DRAFT cards; the student accepts / edits / discards them against the GL-4
 * endpoints. Editing sends the card's expected version (its §2 `version`) and a
 * 409 renders an explicit RE-LOAD UX — never a silent retry/reapply (P13
 * monotonicity). Card text lives in component state ONLY — never web storage
 * (P12). No client events (§1 events are server-emitted).
 */
const GlCardReviewDrawer = ({ questionId, graded = false, glApi = glApiDefault }) => {
  // B2 mount honesty: build flags AND the server's confirmed {allowed:true}
  // (GET /gl/status) — a non-cohort caller never sees a dead affordance.
  const glAllowed = useGlAllowed();
  const active = glAllowed && !!graded && !!questionId;

  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // terminal error for the list load
  const [editing, setEditing] = useState(null); // { cardId, front, back, baseVersion }
  const [rowError, setRowError] = useState(null); // { cardId, glError }
  const [conflict, setConflict] = useState(null); // { cardId } — stale edit, needs reload
  const [busy, setBusy] = useState(false);
  const triggerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await glApi.listDraftCards();
    setLoading(false);
    if (res.ok) {
      // Contract row 3: the BE list is owner-scoped and BARE (no {cards}
      // wrapper, no question param) — this drawer shows the current question's
      // drafts, so filter client-side.
      const all = Array.isArray(res.data) ? res.data : [];
      setCards(all.filter((c) => c && c.questionId === questionId));
    } else {
      setError(res.glError || "unavailable");
    }
  }, [glApi, questionId]);

  // Load the draft count when the surface becomes active (post-grade).
  useEffect(() => {
    if (active) {
      load();
    } else {
      setCards([]);
      setOpen(false);
    }
  }, [active, load]);

  if (!active) {
    return null;
  }

  const draftCount = cards.length;

  const closeEdit = () => {
    setEditing(null);
    setRowError(null);
    setConflict(null);
  };

  const startEdit = (card) => {
    setRowError(null);
    setConflict(null);
    setEditing({
      cardId: card.cardId,
      front: card.front || "",
      back: card.back || "",
      baseVersion: card.version
    });
  };

  const applyUpdatedCard = (updated) => {
    setCards((prev) => prev.map((c) => (c.cardId === updated.cardId ? updated : c)));
  };
  const removeCard = (cardId) => {
    setCards((prev) => prev.filter((c) => c.cardId !== cardId));
  };

  const onAccept = async (card) => {
    setBusy(true);
    setRowError(null);
    const res = await glApi.acceptCard(card.cardId);
    setBusy(false);
    if (res.ok) {
      removeCard(card.cardId); // accepted -> leaves the draft list
    } else {
      setRowError({ cardId: card.cardId, glError: res.glError || "unavailable" });
    }
  };

  const onDiscard = async (card) => {
    setBusy(true);
    setRowError(null);
    const res = await glApi.discardCard(card.cardId);
    setBusy(false);
    if (res.ok) {
      removeCard(card.cardId);
    } else {
      setRowError({ cardId: card.cardId, glError: res.glError || "unavailable" });
    }
  };

  const onSaveEdit = async () => {
    if (!editing) {
      return;
    }
    setBusy(true);
    setRowError(null);
    // Contract row 5: the server edit surface is front/back with the discrete
    // `version` token; the response is the BARE updated CardDto.
    const res = await glApi.editCard(editing.cardId, {
      front: editing.front,
      back: editing.back,
      version: editing.baseVersion
    });
    setBusy(false);
    if (res.ok) {
      applyUpdatedCard(res.data);
      closeEdit();
    } else if (res.glError === "conflict") {
      // P13: the card moved under us. DO NOT reapply — force an explicit reload.
      setConflict({ cardId: editing.cardId });
    } else {
      setRowError({ cardId: editing.cardId, glError: res.glError || "unavailable" });
    }
  };

  // Explicit reload after a conflict: discard the local edit, re-fetch the
  // authoritative cards, and drop the student back to a clean (non-editing) list.
  const onReloadAfterConflict = async () => {
    closeEdit();
    await load();
  };

  const openSheet = () => setOpen(true);
  const closeSheet = () => {
    setOpen(false);
    closeEdit();
  };

  return (
    <Box className="gl-card-review">
      <Button
        ref={triggerRef}
        className="gl-card-review-trigger"
        variant="outlined"
        onClick={openSheet}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={draftCount === 0 && !error && !loading}
      >
        {error
          ? "Review cards — retry"
          : draftCount > 0
          ? `Review cards (${draftCount})`
          : "No review cards"}
      </Button>

      <Dialog
        open={open}
        onClose={closeSheet}
        aria-labelledby="gl-card-review-title"
        className="gl-card-review-sheet"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="gl-card-review-title">Review cards from this question</DialogTitle>
        {/* data-hj-suppress: THE surface that renders a student's WHY verbatim
            ("Your note: …"), plus card front/back derived from it and from the
            licensed explanation. This is a Dialog — a React portal to
            document.body — so it sits outside the exam question wrapper and no
            amount of inheritance from there could reach it. It needs its own. */}
        <DialogContent dividers {...REPLAY_MASK}>
          {loading && <Box className="gl-card-loading" role="status">Loading your cards…</Box>}

          {!loading && error && (
            <Box className="gl-card-terminal" role="alert">
              {TERMINAL_COPY[error] || TERMINAL_COPY.unavailable}
              <Button className="gl-card-retry" variant="text" onClick={load}>Try again</Button>
            </Box>
          )}

          {!loading && !error && draftCount === 0 && (
            <Box className="gl-card-empty">No draft cards to review.</Box>
          )}

          {!loading && !error && cards.map((card) => {
            const isEditing = editing && editing.cardId === card.cardId;
            const isConflict = conflict && conflict.cardId === card.cardId;
            const err = rowError && rowError.cardId === card.cardId ? rowError.glError : null;
            return (
              <Box key={card.cardId} className="gl-card" component="section" aria-label="Draft card">
                <Chip
                  size="small"
                  className={`gl-card-kind gl-card-kind--${card.cardKind}`}
                  label={card.cardKind === "personalized" ? "Personalized" : "Generic"}
                />
                {isConflict ? (
                  <Box className="gl-card-conflict" role="alert">
                    {TERMINAL_COPY.conflict} Reload to see the latest before editing — your
                    unsaved changes won't be applied.
                    <Button className="gl-card-reload" variant="contained" color="primary" style={AA_PRIMARY} onClick={onReloadAfterConflict}>
                      Reload cards
                    </Button>
                  </Box>
                ) : isEditing ? (
                  <Box className="gl-card-edit">
                    <TextField
                      id={`gl-edit-front-${card.cardId}`}
                      label="Front"
                      value={editing.front}
                      onChange={(e) => setEditing((s) => ({ ...s, front: e.target.value }))}
                      multiline
                      fullWidth
                      inputProps={{ autoComplete: "off" }}
                    />
                    <TextField
                      id={`gl-edit-back-${card.cardId}`}
                      label="Back"
                      value={editing.back}
                      onChange={(e) => setEditing((s) => ({ ...s, back: e.target.value }))}
                      multiline
                      fullWidth
                      inputProps={{ autoComplete: "off" }}
                    />
                    {err && <Box className="gl-card-row-error" role="alert">{TERMINAL_COPY[err] || TERMINAL_COPY.unavailable}</Box>}
                    <Box className="gl-card-actions">
                      <Button variant="contained" color="primary" style={AA_PRIMARY} disabled={busy} onClick={onSaveEdit}>Save</Button>
                      <Button variant="text" disabled={busy} onClick={closeEdit}>Cancel</Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Box className="gl-card-face gl-card-front">{card.front}</Box>
                    <Box className="gl-card-face gl-card-back">{card.back}</Box>
                    {card.cardKind === "personalized" && card.whyExcerpt && (
                      <Box className="gl-card-why">Your note: “{card.whyExcerpt}”</Box>
                    )}
                    {err && <Box className="gl-card-row-error" role="alert">{TERMINAL_COPY[err] || TERMINAL_COPY.unavailable}</Box>}
                    <Box className="gl-card-actions">
                      <Button variant="contained" color="primary" style={AA_PRIMARY} disabled={busy} onClick={() => onAccept(card)}>Accept</Button>
                      <Button variant="outlined" disabled={busy} onClick={() => startEdit(card)}>Edit</Button>
                      <Button variant="text" disabled={busy} onClick={() => onDiscard(card)}>Discard</Button>
                    </Box>
                  </>
                )}
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GlCardReviewDrawer;
